"use client";
import React from "react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRouter, useSearchParams } from "next/navigation";
import { LoginSchema } from "@/schemas/auth-schema";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import Link from "next/link";
import { EyeIcon, EyeOffIcon, Loader, TriangleAlertIcon } from "lucide-react";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { signIn } from "next-auth/react";
import { DEFAULT_LOGIN_REDIRECT } from "@/routes";

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const email = searchParams !== null ? searchParams.get("email") : null;
  const [error, setError] = React.useState<string | undefined>("");
  const [passwordVisibility, setPasswordVisibility] = React.useState<boolean>(false);

  const [isPending, startTransition] = React.useTransition();

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: email || "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
    setError("");

    startTransition(async () => {
      try {
        const signInResponse = await signIn("credentials", {
          email: values.email,
          password: values.password,
          redirect: false,
        });

        if (signInResponse && signInResponse.error) {
          setError("Invalid username and password");
        } else {
          router.replace(DEFAULT_LOGIN_REDIRECT);
        }
      } catch {
        setError("Invalid username and password");
      }
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 p-8">
              <div className="flex flex-col gap-8">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-2xl font-bold">Welcome back</h1>
                  <p className="text-balance text-muted-foreground">
                    Login to your <b>Glovebox</b> account
                  </p>
                </div>
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            disabled={isPending}
                            placeholder="john.doe@example.com"
                            type="email"
                            required
                            autoFocus={form.getValues().email === undefined ? true : false}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Password</FormLabel>
                          <Button
                            size="sm"
                            variant="link"
                            asChild
                            className="px-0 font-normal"
                            tabIndex={-1}
                          >
                            <Link href="/auth/reset">Forgot password?</Link>
                          </Button>
                        </div>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              autoFocus={form.getValues().email ? true : false}
                              disabled={isPending}
                              type={passwordVisibility ? "text" : "password"}
                              autoComplete="on"
                              placeholder="******"
                              required
                            />
                            <div
                              className="absolute inset-y-0 right-0 flex cursor-pointer items-center p-3 text-muted-foreground"
                              onClick={() => setPasswordVisibility(!passwordVisibility)}
                            >
                              {React.createElement(passwordVisibility ? EyeOffIcon : EyeIcon, {
                                className: "h-6 w-6",
                              })}
                            </div>
                          </div>
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {error && (
                  <Badge variant="destructive" className="p-2">
                    <TriangleAlertIcon className="h-4 w-4 mr-2" />
                    {error}
                  </Badge>
                )}

                <Button type="submit" className="w-full" disabled={isPending}>
                  {isPending && <Loader className="h-4 w-4 animate-spin mr-2" />}
                  {isPending && "Logging in..."}
                  {!isPending && "Login"}
                </Button>
              </div>
            </form>
          </Form>

          <div className="relative hidden md:block bg-gray-100 dark:bg-gray-100">
            <div className="h-full w-full flex items-center justify-center">
              <Image src="/assets/login_image.jpg" alt="Image" width={500} height={500} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        By clicking continue, you agree to our{" "}
        <Link href="/terms-of-service">Terms of Service</Link> and{" "}
        <Link href="/privacy-policy">Privacy Policy</Link>.
      </div>
    </div>
  );
}
